/**
 * Wire-контракт поиска (`Platform.Contracts/Search`, RP-A6a). Пустой `hits` при
 * 200 — «ничего не нашлось»; сбой службы поиска приходит 503, а не этим ответом
 * (транспорт делает из него ошибку `unavailable`) — экран различает их.
 */
import { fail, flag, integer, isRecord, list, optionalText, record, text, textList, textMap } from '../../../shared/decode'
import { decodeMainPhoto } from '../../common-photos/api/contracts'
import {
  isLineBand,
  isStageOutcome,
  isSearchVerdict,
  type EvidenceItem,
  type HintWord,
  type PhotoJudgeTrace,
  type PhotoPreview,
  type PhotoProcessing,
  type PhotoRecognition,
  type RecognizedLine,
  type SearchGroups,
  type SearchHealth,
  type SearchHints,
  type SearchHit,
  type SearchStageTiming,
  type SearchTiming,
  type SearchTimingSummary,
  type TimingStats,
  type SearchIdentity,
  type SearchResponse,
  type SearchVerdict,
  type TwinGroup,
  type VerdictReason,
} from '../model/types'

function decodeIdentity(raw: unknown, name: string): SearchIdentity {
  const item = record(raw, name)
  return {
    resourceId: text(item.resourceId, `${name}.resourceId`),
    resourceKind: text(item.resourceKind, `${name}.resourceKind`),
    entityKind: text(item.entityKind, `${name}.entityKind`),
    remoteKey: text(item.remoteKey, `${name}.remoteKey`),
    state: optionalText(item.state, `${name}.state`),
  }
}

const optionalNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

/** Свидетельство позиции (`EvidenceItem`): вид, есть ли данные, log-LR и КОД пояснения (фраз сервер не присылает). */
function decodeEvidence(raw: unknown, name: string): EvidenceItem {
  const item = record(raw, name)
  return {
    kind: text(item.kind, `${name}.kind`),
    present: flag(item.present, `${name}.present`),
    logLr: integer(item.logLr, `${name}.logLr`),
    note: text(item.note, `${name}.note`),
  }
}

/** Серия близнецов позиции (`TwinGroupInfo`); `null` — позиция не в серии. */
function decodeTwinGroup(raw: unknown, name: string): TwinGroup | null {
  if (raw === null) return null
  const item = record(raw, name)
  return {
    id: text(item.id, `${name}.id`),
    size: integer(item.size, `${name}.size`),
    differences: textList(item.differences ?? [], `${name}.differences`),
  }
}

function decodeHit(raw: unknown, name: string): SearchHit {
  const item = record(raw, name)
  return {
    productId: text(item.productId, `${name}.productId`),
    name: text(item.name, `${name}.name`),
    sku: optionalText(item.sku, `${name}.sku`),
    barcode: optionalText(item.barcode, `${name}.barcode`),
    confidence: typeof item.confidence === 'number' ? item.confidence : 0,
    matchedBy: textList(item.matchedBy ?? [], `${name}.matchedBy`),
    identities: list(item.identities ?? [], `${name}.identities`, decodeIdentity),
    // Поля RP-FB65: прежний сервер их не шлёт, и у позиции их тогда нет вовсе (а не «пустые»).
    ...(item.photoChecked === undefined ? {} : { photoChecked: flag(item.photoChecked, `${name}.photoChecked`) }),
    ...(item.disagrees === undefined ? {} : { disagrees: flag(item.disagrees, `${name}.disagrees`) }),
    ...(item.twinGroup === undefined ? {} : { twinGroup: decodeTwinGroup(item.twinGroup, `${name}.twinGroup`) }),
    ...(item.evidence === undefined ? {} : { evidence: list(item.evidence, `${name}.evidence`, decodeEvidence) }),
    // Миниатюра общего товара (RP-FB70): прежний сервер поля не шлёт.
    ...(item.mainPhoto === undefined ? {} : { mainPhoto: decodeMainPhoto(item.mainPhoto, `${name}.mainPhoto`) }),
  }
}

/** Неизвестная полоса от сервера читается как «мелкая»: слово остаётся видимым, но веса не получает. */
const DEFAULT_BAND = 'minor'

function decodeLine(raw: unknown, name: string): RecognizedLine {
  const item = record(raw, name)
  const band = text(item.band, `${name}.band`)
  return {
    text: text(item.text, `${name}.text`),
    confidence: typeof item.confidence === 'number' ? item.confidence : 0,
    inRoi: item.inRoi === true,
    weight: typeof item.weight === 'number' ? item.weight : 0,
    band: isLineBand(band) ? band : DEFAULT_BAND,
    ignoredReason: optionalText(item.ignoredReason, `${name}.ignoredReason`),
    focus: item.focus === true,
  }
}

/** Нет поля или `null` — снимок не читали (прежний сервер или сбой чтения), это не «текста нет». */
export function decodeRecognition(raw: unknown): PhotoRecognition | null {
  if (raw === undefined || raw === null) return null
  const item = record(raw, 'recognized')
  return {
    text: optionalText(item.text, 'recognized.text'),
    barcodes: textList(item.barcodes ?? [], 'recognized.barcodes'),
    lines: list(item.lines ?? [], 'recognized.lines', decodeLine),
  }
}

const numberList = (value: unknown): number[] | null =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'number' && Number.isFinite(entry)) ? value : null

function decodePreview(raw: unknown): PhotoPreview | null {
  if (!isRecord(raw)) return null
  const item = raw
  return typeof item.dataBase64 === 'string' && item.dataBase64 !== ''
    ? { contentType: typeof item.contentType === 'string' ? item.contentType : 'image/jpeg', dataBase64: item.dataBase64 }
    : null
}

/** Нет поля или не объект — сервер прежний, обработки в ответе нет: это не ошибка ответа. */
export function decodeProcessing(raw: unknown): PhotoProcessing | null {
  if (raw === undefined || raw === null) return null
  const item = record(raw, 'processing')
  return {
    sourceWidth: optionalNumber(item.sourceWidth),
    sourceHeight: optionalNumber(item.sourceHeight),
    sourceBytes: optionalNumber(item.sourceBytes) ?? 0,
    backgroundRemoved: item.backgroundRemoved === true,
    backgroundNote: typeof item.backgroundNote === 'string' ? item.backgroundNote : '',
    processedPreview: decodePreview(item.processedPreview),
    roi: numberList(item.roi),
    cropWidth: optionalNumber(item.cropWidth),
    cropHeight: optionalNumber(item.cropHeight),
    encoder: optionalText(item.encoder, 'processing.encoder'),
    preprocessingKind: optionalText(item.preprocessingKind, 'processing.preprocessingKind'),
    inputSize: optionalNumber(item.inputSize),
    padColor: numberList(item.padColor),
    encoderInputPreview: decodePreview(item.encoderInputPreview),
    processedPreviewReason: optionalText(item.processedPreviewReason, 'processing.processedPreviewReason'),
    encoderInputPreviewReason: optionalText(item.encoderInputPreviewReason, 'processing.encoderInputPreviewReason'),
    backgroundReason: optionalText(item.backgroundReason, 'processing.backgroundReason'),
    indexMode: optionalText(item.indexMode, 'processing.indexMode'),
    indexRebuildDone: optionalNumber(item.indexRebuildDone),
    indexRebuildTotal: optionalNumber(item.indexRebuildTotal),
    dualQuery: typeof item.dualQuery === 'boolean' ? item.dualQuery : null,
    bestVariant: optionalText(item.bestVariant, 'processing.bestVariant'),
    originalScore: optionalNumber(item.originalScore),
    cutScore: optionalNumber(item.cutScore),
    productBoxStatus: optionalText(item.productBoxStatus, 'processing.productBoxStatus'),
  }
}

/** Что получила модель проверки; нет поля — проверки не было. */
function decodeJudge(raw: unknown): PhotoJudgeTrace | null {
  if (raw === undefined || raw === null) return null
  const item = record(raw, 'judge')
  return {
    inputKind: text(item.inputKind, 'judge.inputKind'),
    input: decodePreview(item.input),
    inputReason: optionalText(item.inputReason, 'judge.inputReason'),
    candidates: integer(item.candidates, 'judge.candidates'),
    candidatesWithPhoto: integer(item.candidatesWithPhoto, 'judge.candidatesWithPhoto'),
    model: optionalText(item.model, 'judge.model'),
  }
}

/** Нет поля — сервер прежний (плоская выдача); неизвестное слово — ошибка ответа, а не молчаливая подмена вердикта. */
function decodeVerdict(raw: unknown): SearchVerdict | null {
  if (raw === undefined || raw === null) return null
  const value = text(raw, 'verdict')
  return isSearchVerdict(value) ? value : fail(`поле verdict — неизвестное значение «${value}»`)
}

const nonEmpty = (value: string | null): string | null => (value === null || value.trim() === '' ? null : value)

function decodeWord(raw: unknown, name: string): HintWord {
  const item = record(raw, name)
  const band = text(item.band, `${name}.band`)
  return { text: text(item.text, `${name}.text`), band: isLineBand(band) ? band : DEFAULT_BAND }
}

/** Кадр на сервере не хранится: номер кадра для цепочки окон присваивает клиент. */
function decodeHints(raw: unknown): SearchHints | null {
  if (raw === undefined || raw === null) return null
  const item = record(raw, 'hints')
  return {
    suggestedName: nonEmpty(optionalText(item.suggestedName, 'hints.suggestedName')),
    barcode: nonEmpty(optionalText(item.barcode, 'hints.barcode')),
    barcodeVerified: item.barcodeVerified === true,
    words: list(item.words ?? [], 'hints.words', decodeWord),
  }
}

function decodeGroups(raw: unknown): SearchGroups {
  if (raw === undefined || raw === null) return { confident: [], possible: [], belowThreshold: 0 }
  const item = record(raw, 'groups')
  return {
    confident: list(item.confident ?? [], 'groups.confident', decodeHit),
    possible: list(item.possible ?? [], 'groups.possible', decodeHit),
    belowThreshold: typeof item.belowThreshold === 'number' && Number.isFinite(item.belowThreshold) ? item.belowThreshold : 0,
    weak: list(item.weak ?? [], 'groups.weak', decodeHit),
    listMin: optionalNumber(item.listMin),
  }
}

/** Причина вердикта (`VerdictReason`): код и числа для подстановки (строки); нет `params` — чисел нет. */
function decodeReason(raw: unknown, name: string): VerdictReason {
  const item = record(raw, name)
  return { code: text(item.code, `${name}.code`), params: item.params === undefined || item.params === null ? {} : textMap(item.params, `${name}.params`) }
}

export function decodeSearchResponse(raw: unknown): SearchResponse {
  const item = record(raw, 'search')
  const verdict = decodeVerdict(item.verdict)
  const groups = decodeGroups(item.groups)
  return {
    scope: text(item.scope, 'scope'),
    total: integer(item.total, 'total'),
    // С вердиктом плоского `hits` может не быть: тогда выдача — это обе группы.
    hits: verdict !== null && item.hits === undefined ? [...groups.confident, ...groups.possible] : list(item.hits, 'hits', decodeHit),
    unresolved: integer(item.unresolved ?? 0, 'unresolved'),
    recognized: decodeRecognition(item.recognized),
    visualContrast: optionalNumber(item.visualContrast),
    processing: decodeProcessing(item.processing),
    verdict,
    groups,
    hints: decodeHints(item.hints),
    verdictStamp: nonEmpty(optionalText(item.verdictStamp, 'verdictStamp')),
    searchId: nonEmpty(optionalText(item.searchId, 'searchId')),
    judge: decodeJudge(item.judge),
    ...(item.reasons === undefined || item.reasons === null ? {} : { reasons: list(item.reasons, 'reasons', decodeReason) }),
  }
}

export function decodeSearchHealth(raw: unknown): SearchHealth {
  const item = record(raw, 'health')
  return {
    status: text(item.status, 'status'),
    components: list(item.components ?? [], 'components', (entry, name) => {
      const component = record(entry, name)
      return {
        name: text(component.name, `${name}.name`),
        status: text(component.status, `${name}.status`),
        detail: optionalText(component.detail, `${name}.detail`),
      }
    }),
  }
}


function decodeTimingStats(raw: unknown, name: string): TimingStats {
  const item = record(raw, name)
  return {
    count: integer(item.count, `${name}.count`),
    medianMs: integer(item.medianMs, `${name}.medianMs`),
    p90Ms: integer(item.p90Ms, `${name}.p90Ms`),
    maxMs: integer(item.maxMs, `${name}.maxMs`),
  }
}

const optionalTimingStats = (raw: unknown, name: string): TimingStats | null => (raw === null || raw === undefined ? null : decodeTimingStats(raw, name))

function decodeStageTiming(raw: unknown, name: string): SearchStageTiming {
  const item = record(raw, name)
  const outcome = text(item.outcome, `${name}.outcome`)
  return {
    stage: text(item.stage, `${name}.stage`),
    startMs: integer(item.startMs, `${name}.startMs`),
    durationMs: integer(item.durationMs, `${name}.durationMs`),
    outcome: isStageOutcome(outcome) ? outcome : fail(`${name}.outcome — неизвестный исход «${outcome}»`),
  }
}

/** Время одного поиска по этапам (`GET search/timings/{searchId}`). */
export function decodeSearchTiming(raw: unknown): SearchTiming {
  const item = record(raw, 'timing')
  return {
    searchId: text(item.searchId, 'searchId'),
    startedAt: text(item.startedAt, 'startedAt'),
    totalMs: integer(item.totalMs, 'totalMs'),
    firstListMs: integer(item.firstListMs, 'firstListMs'),
    stages: list(item.stages, 'stages', decodeStageTiming),
  }
}

/** Сводка времени боевых поисков за период (`GET search/timings`). */
export function decodeSearchTimingSummary(raw: unknown): SearchTimingSummary {
  const item = record(raw, 'timingSummary')
  return {
    from: text(item.from, 'from'),
    to: text(item.to, 'to'),
    searches: integer(item.searches, 'searches'),
    truncated: flag(item.truncated, 'truncated'),
    total: optionalTimingStats(item.total, 'total'),
    firstList: optionalTimingStats(item.firstList, 'firstList'),
    stages: list(item.stages, 'stages', (entry, name) => {
      const stage = record(entry, name)
      return { stage: text(stage.stage, `${name}.stage`), stats: decodeTimingStats(stage.stats, `${name}.stats`) }
    }),
  }
}
